/**
 * Pure helpers for the personalized price-preview surfaces (day-view
 * browse rows + checkout lines/totals). Lives in `lib/` (no React, no
 * `@/api` runtime imports) so the pages and the unit tests share one
 * implementation. Structural types only — the wire shapes are declared
 * in `@/api/family-orders`; these helpers accept anything carrying the
 * money fields they read.
 */

export interface PreviewMoneyLine {
  menuItemId: string
  studentUserId: string
  /** Special-diet axis — part of the lookup key (see `previewLineKey`). */
  specialDietAccommodationId?: string | null
  /** LINE-total cents the school/program covers. */
  sponsoredCents: number
  /** LINE-total cents the family pays. */
  netCents: number
}

export interface PreviewTotals {
  grossCents: number
  subsidyCents: number
  /** Family share BEFORE tax (gross − subsidy). */
  netCents: number
  /**
   * Server-computed sales tax on top of `netCents` ($0 for campuses
   * with no configured rate). The payable total every cart surface
   * renders is `netCents + taxCents` — see `previewPayableCents`.
   */
  taxCents: number
}

/**
 * What the family will actually be charged for the previewed lines —
 * net of subsidies, INCLUSIVE of tax. Every single-amount cart surface
 * (week rail, mobile bar, day-view footer, checkout total) renders
 * this so the shown number always equals the wallet debit.
 */
export function previewPayableCents(totals: Pick<PreviewTotals, 'netCents' | 'taxCents'>): number {
  return totals.netCents + totals.taxCents
}

/**
 * Lookup key for a previewed line. `scope` disambiguates the sitting —
 * the day view uses the mealtime, checkout uses `${date}::${mealtime}`.
 * `specialDietAccommodationId` is part of the key: a student can hold BOTH a
 * regular and an accommodation line for the same menu item in one sitting,
 * and without the axis the later line silently overwrites the first.
 */
export function previewLineKey(
  scope: string,
  studentUserId: string,
  menuItemId: string,
  specialDietAccommodationId?: string | null,
): string {
  const base = `${scope}::${studentUserId}::${menuItemId}`
  return specialDietAccommodationId ? `${base}::${specialDietAccommodationId}` : base
}

/**
 * Flatten per-sitting preview responses into one line lookup keyed by
 * `previewLineKey`. Later entries win on duplicate keys (shouldn't
 * happen — a sitting's lines are unique per (student, menuItem)).
 */
export function buildPreviewLookup<T extends PreviewMoneyLine>(
  entries: ReadonlyArray<{ scope: string; lines: readonly T[] } | null | undefined>,
): Map<string, T> {
  const out = new Map<string, T>()
  for (const entry of entries) {
    if (!entry) {
      continue
    }
    for (const line of entry.lines) {
      out.set(previewLineKey(entry.scope, line.studentUserId, line.menuItemId, line.specialDietAccommodationId), line)
    }
  }
  return out
}

/**
 * Sum per-group preview totals into one block. Returns `null` when the
 * list is empty or ANY group is still missing (loading / errored) — the
 * caller falls back to the gross-only display rather than showing a
 * partial "You pay".
 */
export function combinePreviewTotals(totals: ReadonlyArray<PreviewTotals | null | undefined>): PreviewTotals | null {
  if (totals.length === 0) {
    return null
  }
  const acc: PreviewTotals = { grossCents: 0, subsidyCents: 0, netCents: 0, taxCents: 0 }
  for (const t of totals) {
    if (!t) {
      return null
    }
    acc.grossCents += t.grossCents
    acc.subsidyCents += t.subsidyCents
    acc.netCents += t.netCents
    // Deploy-skew guard: a still-cached pre-tax response (30s staleTime)
    // lacks the field — treat it as $0 rather than poisoning the sum.
    acc.taxCents += (t.taxCents as number | undefined) ?? 0
  }
  return acc
}

/**
 * Pair `useQueries` preview results with their sitting via the scope
 * embedded in each result's OWN data — never by array index. Results
 * arrive positionally, and `keepPreviousData` can hand a position
 * retained data from a DIFFERENT query key while the group list changes
 * shape; index-pairing would then stamp one sitting's prices/allowance
 * onto another. Self-identifying data cannot be misattributed. Missing
 * results (still loading / errored) are dropped.
 */
export function scopedPreviewEntries<T extends PreviewMoneyLine, S extends PreviewStudentAllowance>(
  results: ReadonlyArray<{
    data?: { scope: string; preview: { lines: readonly T[]; students?: readonly S[] } } | undefined
  }>,
): Array<{ scope: string; lines: readonly T[]; students: readonly S[] }> {
  return results.flatMap((result) =>
    result.data
      ? [{ scope: result.data.scope, lines: result.data.preview.lines, students: result.data.preview.students ?? [] }]
      : [],
  )
}

/**
 * The display treatment for a previewed line: `covered` (fully
 * sponsored → strikethrough gross, $0 net), `reduced` (partially
 * sponsored → strikethrough gross, lower net), or `null` (no sponsorship
 * — render the plain gross price, including the allowance-exhausted
 * case, which deliberately shows NO badge).
 */
export function previewBadge(
  line: Pick<PreviewMoneyLine, 'sponsoredCents' | 'netCents'>,
): 'covered' | 'reduced' | null {
  if (line.sponsoredCents <= 0) {
    return null
  }
  return line.netCents === 0 ? 'covered' : 'reduced'
}

/** Per-student allowance state for one sitting (see the preview wire type). */
export interface PreviewStudentAllowance {
  studentUserId: string
  subsidizedMealsPerSitting: number
  subsidizedUnitsUsed: number
  remainingSubsidizedUnits: number
}

/**
 * Lookup key for a student's allowance state within a sitting scope
 * (same scope strings as `previewLineKey`).
 */
export function sittingStudentKey(scope: string, studentUserId: string): string {
  return `${scope}::student::${studentUserId}`
}

/**
 * Remaining subsidized units for a student's sitting, preferring the
 * CART-aware entry (submit-mode preview over the cart's lines — counts
 * prior orders + cart contents) and falling back to the browse
 * response's prior-orders-only entry when the cart has no lines for
 * the sitting. `null` = no subsidy applies (or nothing loaded yet) —
 * callers keep the per-line preview display untouched in that case.
 */
export function resolveSittingAllowance(
  cartEntry: PreviewStudentAllowance | null | undefined,
  browseStudents: readonly PreviewStudentAllowance[] | null | undefined,
  studentUserId: string,
): number | null {
  if (cartEntry) {
    return cartEntry.remainingSubsidizedUnits
  }
  const fromBrowse = browseStudents?.find((student) => student.studentUserId === studentUserId)
  return fromBrowse ? fromBrowse.remainingSubsidizedUnits : null
}
